import { Dialog } from '@hai/ui-react';
import { ButtonStateType, FileUploadStages, IUploadState } from '@hai/ui-react/dist/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadZone } from 'src/js/component/base/upload-zone';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { constant } from 'src/js/util/constant';

const UPLOAD_MULTIPLE = false;

interface Props {
  onCancel: VoidFunction;
  onImport: (file: File) => Promise<any>;
  onImportSuccess: VoidFunction;
}

export const LogoImportModal: React.FunctionComponent<Props> = ({ onCancel, onImport, onImportSuccess }) => {
  const { t } = useTranslation();

  const [uploadBtnState, setUploadBtnState] = useState<ButtonStateType>();
  const [uploadBtnDisabled, setUploadBtnDisabled] = useState(false);
  const [uploadState, setUploadState] = useState<IUploadState>({
    stage: FileUploadStages.NONE,
    infiniteLoading: true,
  });

  const onUploadError = (newuploadState: IUploadState) => {
    setUploadState({ ...newuploadState, animationTime: 0.5 });
  };

  const handleStartUpload = (files: File[]): Promise<void> | void => {
    if (Array.isArray(files) && files.length > constant.maxSimultaneousFileUploads) {
      createAndDispatchNotification(
        t('general.maxSimultaneousFileUploads', { max: constant.maxSimultaneousFileUploads }),
        NotificationVariant.ERROR,
      );
      return;
    }
    setUploadBtnDisabled(true);
    setUploadState((prev: IUploadState) => {
      return { ...prev, stage: FileUploadStages.INPROGRESS };
    });
    if (Array.isArray(files)) {
      const promises = files.map((oneFile: File) => {
        return onImport(oneFile);
      });
      Promise.all(promises).then((response) => {
        const anyError = response.filter((r) => !r.ok).length > 0;
        let newState = FileUploadStages.COMPLETED;
        if (anyError) {
          newState = FileUploadStages.ERROR;
        }
        setUploadState((prev: IUploadState) => {
          return { ...prev, stage: newState };
        });
        setUploadBtnState(undefined);
        if (anyError) {
          setUploadBtnDisabled(false);
          onCancel(); // close modal until UX improves NKE-2673
        } else {
          createAndDispatchNotification(
            t('logos.notifications.importSuccessMsg', { count: response.length }),
            NotificationVariant.SUCCESS,
          );
          onImportSuccess();
        }
      });
    } else {
      return onImport(files).then((response) => {
        const anyError = !response.ok;
        let newState = FileUploadStages.COMPLETED;
        if (anyError) {
          newState = FileUploadStages.ERROR;
        }
        setUploadState((prev: IUploadState) => {
          return { ...prev, stage: newState };
        });
        setUploadBtnState(undefined);
        if (anyError) {
          setUploadBtnDisabled(false);
          if (response.problem === 'NETWORK_ERROR' || response.problem === 'CONNECTION_ERROR') {
            return;
          } else {
            // server error, follow through and display notification
            onCancel(); // close modal until UX improves NKE-2673
          }
        } else {
          createAndDispatchNotification(
            t('logos.notifications.importSuccessMsg', { count: 1 }),
            NotificationVariant.SUCCESS,
          );
          onImportSuccess();
        }
      });
    }
  };

  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    // FIXME this is not working, is the upload zone gobbing up the events?
    if (e.key === 'Enter') {
      const button = document.getElementById('id-button-upload');
      if (button) {
        e.preventDefault();
        button.click();
      }
    }
  };

  return (
    <Dialog
      accentColor={constant.productColor}
      title={t('logos.import.title')}
      onClose={onCancel}
      onKeyUp={handleOnKeyUp}
      show={true}
      dialogType="activity"
      headerIcon="StillImage"
      icon="none"
      content={
        <UploadZone
          accept={{ '': constant.logoFileTypes }}
          onUpload={handleStartUpload}
          onCancel={onCancel}
          multiple={UPLOAD_MULTIPLE}
          uploadBtnText={t('general.import')}
          uploadBtnState={uploadBtnState}
          uploadBtnDisabled={uploadBtnDisabled}
          uploadErrorMessage={t('logos.notifications.importNetworkError')}
          uploadState={uploadState}
          onUploadError={onUploadError}
          onKeyUp={handleOnKeyUp}
        />
      }
    />
  );
};
